import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Jodit } from 'jodit';
import { AtSign, Megaphone, Settings, User } from 'lucide-react';

export type MentionVariable = {
    ref: string;
    name: string;
    description: string;
    category: 'Contact' | 'Campaign' | 'System';
};

export type VariablePickerProps = {
    joditRef: React.MutableRefObject<Jodit | null>;
    anchorEl: HTMLSpanElement | null;
    query: string;
    onClose: () => void;
};

export const MENTION_VARIABLES: MentionVariable[] = [
    { ref: '{{@first_name}}', name: 'First Name', description: "Recipient's first name", category: 'Contact' },
    { ref: '{{@last_name}}', name: 'Last Name', description: "Recipient's last name", category: 'Contact' },
];

type CategoryMeta = {
    icon: React.ReactNode;
    pill: string;
    accent: string;
};

const CATEGORY_META: Record<MentionVariable['category'], CategoryMeta> = {
    Contact: {
        icon: <User className="w-3 h-3" />,
        pill: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
        accent: 'bg-blue-50 dark:bg-blue-950/30',
    },
    Campaign: {
        icon: <Megaphone className="w-3 h-3" />,
        pill: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300',
        accent: 'bg-violet-50 dark:bg-violet-950/30',
    },
    System: {
        icon: <Settings className="w-3 h-3" />,
        pill: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
        accent: 'bg-emerald-50 dark:bg-emerald-950/30',
    },
};

const MIN_PICKER_MARGIN = 8;
const PICKER_WIDTH = 300;
const PICKER_MAX_HEIGHT = 360;

function buildVariableChip(ref: string): string {
    return `<span data-rich-mentions="${ref}" class="mention-variable" contenteditable="false">${ref}</span>&nbsp;`;
}

export default function VariablePicker({ joditRef, anchorEl, query, onClose }: VariablePickerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [activeIdx, setActiveIdx] = useState(0);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    const filtered = useMemo(() => {
        const q = query.replace(/^@/, '').toLowerCase().trim();
        if (!q) return MENTION_VARIABLES;
        return MENTION_VARIABLES.filter(
            (v) =>
                v.name.toLowerCase().includes(q) ||
                v.ref.toLowerCase().includes(q) ||
                v.description.toLowerCase().includes(q) ||
                v.category.toLowerCase().includes(q)
        );
    }, [query]);

    useEffect(() => {
        if (!anchorEl || filtered.length === 0) {
            setPos(null);
            return;
        }

        const rect = anchorEl.getBoundingClientRect();
        const selectionRect = window
            .getSelection()
            ?.rangeCount
            ? window.getSelection()?.getRangeAt(0).getBoundingClientRect()
            : null;
        const anchorRect =
            rect.width || rect.height || rect.top || rect.left
                ? rect
                : selectionRect;

        if (!anchorRect) {
            setPos(null);
            return;
        }

        const top = Math.min(
            Math.max(anchorRect.bottom + 6, MIN_PICKER_MARGIN),
            window.innerHeight - PICKER_MAX_HEIGHT - MIN_PICKER_MARGIN
        );
        const left = Math.min(
            Math.max(anchorRect.left, MIN_PICKER_MARGIN),
            window.innerWidth - PICKER_WIDTH - MIN_PICKER_MARGIN
        );

        setPos({ top, left });
    }, [anchorEl, filtered.length, query]);

    const grouped = useMemo(() => {
        const map: Partial<Record<MentionVariable['category'], MentionVariable[]>> = {};
        for (const v of filtered) {
            (map[v.category] ??= []).push(v);
        }
        return map;
    }, [filtered]);

    useEffect(() => { setActiveIdx(0); }, [filtered]);

    useEffect(() => {
        listRef.current
            ?.querySelector<HTMLElement>("[aria-selected='true']")
            ?.scrollIntoView({ block: 'nearest' });
    }, [activeIdx]);

    const insertVariable = useCallback(
        (variable: MentionVariable) => {
            const jodit = joditRef.current;
            if (!jodit) return;
            try {
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                    const range = sel.getRangeAt(0);
                    const safeStart = Math.max(0, range.startOffset - query.length);
                    range.setStart(range.startContainer, safeStart);
                    range.deleteContents();
                }
            } catch { }

            jodit.selection.insertHTML(buildVariableChip(variable.ref));
            onClose();
        },
        [joditRef, query, onClose]
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!pos) return;
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setActiveIdx((i) => Math.max(i - 1, 0));
                    break;
                case 'Enter':
                case 'Tab':
                    e.preventDefault();
                    if (filtered[activeIdx]) insertVariable(filtered[activeIdx]);
                    break;
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
            }
        },
        [pos, filtered, activeIdx, insertVariable, onClose]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [handleKeyDown]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    if (!pos || filtered.length === 0) return null;

    let flatIdx = -1;

    return (
        <div
            ref={containerRef}
            role="listbox"
            aria-label="Template variable suggestions"
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 99999, width: PICKER_WIDTH, maxHeight: PICKER_MAX_HEIGHT }}
            className="flex flex-col overflow-hidden rounded-xl shadow-2xl bg-surface dark:bg-[#13182a] border border-line/80 dark:border-slate-700/60 animate-picker-in"
        >
            <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-line-subtle dark:border-slate-700/50">
                <div className="flex items-center gap-1.5">
                    <span className="flex items-center justify-center w-5 h-5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-500 dark:text-indigo-400">
                        <AtSign className="w-3 h-3" />
                    </span>
                    <span className="text-[11px] font-bold text-gray-700 dark:text-slate-200 tracking-wide">
                        Insert Variable
                    </span>
                </div>
                <span className="text-[10px] font-medium text-content-subtle dark:text-slate-500 tabular-nums">
                    {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
                </span>
            </div>

            <div
                ref={listRef}
                className="flex-1 overflow-y-auto overscroll-contain py-1"
                style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(99,102,241,.2) transparent' }}
            >
                {(Object.entries(grouped) as [MentionVariable['category'], MentionVariable[]][]).map(
                    ([category, vars]) => {
                        const meta = CATEGORY_META[category];
                        return (
                            <div key={category}>
                                <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1">
                                    <span className={`flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${meta.pill}`}>
                                        {meta.icon}
                                        {category}
                                    </span>
                                </div>

                                {vars.map((variable) => {
                                    flatIdx++;
                                    const idx = flatIdx;
                                    const isActive = idx === activeIdx;

                                    return (
                                        <div
                                            key={variable.ref}
                                            role="option"
                                            aria-selected={isActive}
                                            onMouseEnter={() => setActiveIdx(idx)}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                insertVariable(variable);
                                            }}
                                            className={[
                                                'flex items-center gap-3 px-3 py-2 mx-1 rounded-lg cursor-pointer select-none transition-all duration-75',
                                                isActive
                                                    ? `${meta.accent} shadow-sm`
                                                    : 'hover:bg-gray-50 dark:hover:bg-slate-800/40',
                                            ].join(' ')}
                                        >
                                            <span className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg ${meta.pill}`}>
                                                {meta.icon}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[12.5px] font-semibold text-gray-800 dark:text-slate-100 leading-tight truncate">
                                                    {variable.name}
                                                </p>
                                                <p className="text-2xs text-content-subtle dark:text-slate-500 leading-tight truncate">
                                                    {variable.description}
                                                </p>
                                            </div>

                                            <span className="flex-shrink-0 text-[9px] font-mono font-semibold text-indigo-500 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200/60 dark:border-indigo-700/40 px-1.5 py-0.5 rounded max-w-[80px] truncate">
                                                {variable.ref}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    }
                )}
            </div>
        </div>
    );
}
